import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  InputAdornment,
  TablePagination,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { API_ENDPOINTS, SHOW_STAGING_DEFAULT_PASSWORD_HINT, STAGING_DEFAULT_USER_PASSWORD } from '../constants/api';
import { useState, useEffect, useMemo, useCallback } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AppLayout from '../components/AppLayout';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import CakeIcon from '@mui/icons-material/Cake';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import BusinessIcon from '@mui/icons-material/Business';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getRoleBadgeColor } from '../utils/roleBadge';
import { formatDate, formatCalendarDateIso } from '../utils/dateFormat';
import { isMissingCompanyForLeave } from '../utils/userCompanyForLeave';

import type { User } from '../services/authService';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotUpdate, couldNotCreate, permissionCopy, genericCopy } from '../utils/userFacingCopy';

interface RoleTemplate {
  id: string;
  name: string;
  description: string;
}

interface ManagerOption {
  id: string;
  fullName: string;
  email: string;
}

interface ReferenceDataOption {
  id: string;
  name: string;
}

interface CompanyOption extends ReferenceDataOption {
  countryCode?: string;
}

interface EditUserForm {
  firstName: string;
  lastName: string;
  roleTemplateId: string;
  managerId: string;
  companyId: string;
  businessUnitId: string;
  departmentIds: string[];
  isActive: boolean;
  roles: string[];
  dateOfBirth: string;
  dateOfEngagement: string;
  dateOfExit: string;
}

interface AddUserForm {
  email: string;
  firstName: string;
  lastName: string;
  roleTemplateId: string;
  managerId: string;
  companyId: string;
  businessUnitId: string;
  departmentIds: string[];
  roles: string[];
  dateOfBirth: string;
  dateOfEngagement: string;
}

const AVAILABLE_ROLES = ['Employee', 'Intern', 'TeamLead', 'Manager', 'SeniorManager', 'Administrator', 'ReportingExempt'];

const ROLE_DESCRIPTIONS: Record<string, { title: string; description: string; permissions: string[] }> = {
  Employee: {
    title: 'Employee',
    description: 'Base role for all users. Employees can view and manage their own reviews, check-ins, and profile information.',
    permissions: [
      'View and complete own performance reviews',
      'View and manage own check-ins',
      'View own profile and audit log',
      'View organisational chart (all views)',
      'Nominate peers for feedback (if enabled)',
    ],
  },
  Intern: {
    title: 'Intern',
    description: 'Developing employees who participate in the IDP and review process, but are excluded from KPI-based reporting metrics.',
    permissions: [
      'All Employee permissions',
      'Included in IDP process, reminders, and manager IDP review confirmation',
      'Performance review is created each cycle',
      'Objective KPI measures are not attached to review forms',
      'Excluded from organisation-level review reporting and KPI completion metrics',
    ],
  },
  TeamLead: {
    title: 'Team Lead',
    description: 'Manages a team of direct reports. Can perform reviews and check-ins for direct reports only (no hierarchical view).',
    permissions: [
      'All Employee permissions',
      'Manage reviews and check-ins for direct reports only',
      'Change role templates for direct reports',
      'View team dashboard with direct reports only',
      'Add peer reviewers on direct reports\' reviews (if peer review enabled)',
    ],
  },
  Manager: {
    title: 'Manager',
    description: 'Manages teams with hierarchical visibility. Can see and manage all employees in their hierarchy (through team leads down to employees).',
    permissions: [
      'All TeamLead permissions',
      'Hierarchical view: see all employees in reporting chain (through team leads)',
      'Manage reviews and check-ins for entire hierarchy',
      'Change role templates for any employee or team lead in hierarchy',
      'View team dashboard with hierarchical data',
    ],
  },
  SeniorManager: {
    title: 'Senior Manager',
    description: 'Manager capabilities plus organisational-level reporting and dashboards. Cannot perform reference data updates (companies, business units, departments).',
    permissions: [
      'All Manager permissions',
      'Organisational-level dashboards and reporting',
      'View all reviews across the organisation',
      'View organisational check-in analytics',
      'View system-wide audit logs',
      'Cannot update reference data (Admin only)',
    ],
  },
  Administrator: {
    title: 'Administrator',
    description: 'Full system access with all permissions including user management, reference data updates, and system configuration.',
    permissions: [
      'All SeniorManager permissions',
      'Manage all users (create, edit, activate/deactivate)',
      'Manage role templates and measures',
      'Manage review cycles (create, start, close)',
      'Update reference data (companies, business units, departments)',
      'Configure system settings (date & locale, review cycles, review policy) via Settings page',
      'Run check-in scheduling and reminders',
      'View all email logs and audit events',
    ],
  },
  ReportingExempt: {
    title: 'Reporting Exempt',
    description: 'Excluded from statistics and reporting. Use for roles (e.g. CEO) who do not have reviews or IDPs and would skew completion rates.',
    permissions: [
      'Not included in review completion or IDP completion calculations',
      'No reviews created when a cycle starts',
      'Excluded from dashboard lists and org/team IDP counts',
      'Can still have other roles (e.g. Administrator) for system access',
    ],
  },
};

type SortField = 'name' | 'email' | 'roles' | 'roleTemplate' | 'manager' | 'status' | 'dateOfBirth' | 'dateOfEngagement' | 'lastLogin';

export default function UserManagementPage() {
  const navigate = useNavigate();
  const { displayTimeZoneId } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [roleTemplates, setRoleTemplates] = useState<RoleTemplate[]>([]);
  const [managers, setManagers] = useState<ManagerOption[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [businessUnits, setBusinessUnits] = useState<ReferenceDataOption[]>([]);
  const [departments, setDepartments] = useState<ReferenceDataOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedManager, setSelectedManager] = useState<ManagerOption | null>(null);
  const [updating, setUpdating] = useState(false);
  const [editForm, setEditForm] = useState<EditUserForm>({
    firstName: '',
    lastName: '',
    roleTemplateId: '',
    managerId: '',
    companyId: '',
    businessUnitId: '',
    departmentIds: [],
    isActive: true,
    roles: [],
    dateOfBirth: '',
    dateOfEngagement: '',
    dateOfExit: '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [profileCompleteFilter, setProfileCompleteFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField | null>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState<AddUserForm>({
    email: '',
    firstName: '',
    lastName: '',
    roleTemplateId: '',
    managerId: '',
    companyId: '',
    businessUnitId: '',
    departmentIds: [],
    roles: ['Employee'],
    dateOfBirth: '',
    dateOfEngagement: '',
  });
  const [addFormManager, setAddFormManager] = useState<ManagerOption | null>(null);
  const [addError, setAddError] = useState<string>('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');

        // Check if user is admin or manager
        const userResponse = await apiService.get<User>(API_ENDPOINTS.AUTH.ME);
        const currentUserData = userResponse.data;
        setCurrentUser(currentUserData);
        const isAdmin = currentUserData.roles.includes('Administrator');
        const isManager = currentUserData.roles.some((r: string) =>
          r === 'Manager' || r === 'SeniorManager'
        );
        if (!isAdmin && !isManager) {
          setError(permissionCopy.administratorOrManager);
          return;
        }

        // Load users
        const usersResponse = await apiService.get<User[]>(API_ENDPOINTS.USER_MANAGEMENT.BASE);
        setUsers(usersResponse.data);

        // Load role templates
        const rolesResponse = await apiService.get<RoleTemplate[]>(API_ENDPOINTS.ROLES.BASE);
        setRoleTemplates(rolesResponse.data);

        // Load managers
        const managersResponse = await apiService.get<ManagerOption[]>(API_ENDPOINTS.USERS.MANAGERS);
        setManagers(managersResponse.data);

        // Load reference data
        const [companiesResponse, businessUnitsResponse, departmentsResponse] = await Promise.all([
          apiService.get<ReferenceDataOption[]>(API_ENDPOINTS.REFERENCE_DATA.COMPANIES.BASE),
          apiService.get<ReferenceDataOption[]>(API_ENDPOINTS.REFERENCE_DATA.BUSINESS_UNITS.BASE),
          apiService.get<ReferenceDataOption[]>(API_ENDPOINTS.REFERENCE_DATA.DEPARTMENTS.BASE),
        ]);
        setCompanies(companiesResponse.data);
        setBusinessUnits(businessUnitsResponse.data);
        // Sort departments alphabetically by name
        const sortedDepartments = [...departmentsResponse.data].sort((a, b) => a.name.localeCompare(b.name));
        setDepartments(sortedDepartments);
      } catch (err: unknown) {
        console.error('Failed to load user management data:', err);
        setError(getApiErrorMessage(err, genericCopy.userManagement));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    const manager = managers.find(m => m.id === user.managerId) || null;
    setSelectedManager(manager);
    // Ensure Employee role is always included when loading user for editing
    const userRoles = Array.isArray(user.roles) ? [...user.roles] : [];
    if (!userRoles.includes('Employee')) {
      userRoles.push('Employee');
    }
    const toDateInputValue = (iso?: string) => formatCalendarDateIso(iso);
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      roleTemplateId: user.roleTemplateId || '',
      managerId: user.managerId || '',
      companyId: user.companyId || '',
      businessUnitId: user.businessUnitId || '',
      departmentIds: user.departmentIds || (user.departmentId ? [user.departmentId] : []),
      isActive: user.isActive,
      roles: userRoles,
      dateOfBirth: toDateInputValue(user.dateOfBirth),
      dateOfEngagement: toDateInputValue(user.dateOfEngagement),
      dateOfExit: toDateInputValue(user.dateOfExit),
    });
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setSelectedManager(null);
    setEditForm({
      firstName: '',
      lastName: '',
      roleTemplateId: '',
      managerId: '',
      companyId: '',
      businessUnitId: '',
      departmentIds: [],
      isActive: true,
      roles: [],
      dateOfBirth: '',
      dateOfEngagement: '',
      dateOfExit: '',
    });
  };

  const handleAddOpen = () => {
    setAddError('');
    setAddForm({
      email: '',
      firstName: '',
      lastName: '',
      roleTemplateId: '',
      managerId: '',
      companyId: '',
      businessUnitId: '',
      departmentIds: [],
      dateOfBirth: '',
      dateOfEngagement: '',
      roles: ['Employee'],
    });
    setAddFormManager(null);
    setAddDialogOpen(true);
    setError('');
  };

  const handleAddClose = () => {
    setAddDialogOpen(false);
    setAddError('');
    setAddForm({
      email: '',
      firstName: '',
      lastName: '',
      roleTemplateId: '',
      managerId: '',
      companyId: '',
      businessUnitId: '',
      departmentIds: [],
      roles: ['Employee'],
      dateOfBirth: '',
      dateOfEngagement: '',
    });
    setAddFormManager(null);
  };

  const isAddFormComplete =
    !!addForm.email.trim() &&
    !!addForm.firstName.trim() &&
    !!addForm.lastName.trim() &&
    !!addForm.dateOfBirth.trim() &&
    !!addForm.dateOfEngagement.trim() &&
    !!addForm.roleTemplateId.trim() &&
    Array.isArray(addForm.roles) &&
    addForm.roles.length > 0 &&
    !!addForm.managerId.trim() &&
    !!addForm.companyId.trim() &&
    !!addForm.businessUnitId.trim() &&
    addForm.departmentIds.length > 0;

  const handleAddSubmit = async () => {
    if (!isAddFormComplete) {
      setAddError('Complete all required fields before adding a user.');
      return;
    }
    setUpdating(true);
    setAddError('');
    try {
      const rolesToSave = [...addForm.roles];
      if (!rolesToSave.includes('Employee')) {
        rolesToSave.push('Employee');
      }
      const payload: Record<string, unknown> = {
        email: addForm.email.trim(),
        firstName: addForm.firstName.trim(),
        lastName: addForm.lastName.trim(),
        roles: rolesToSave,
        roleTemplateId: addForm.roleTemplateId.trim(),
        managerId: addForm.managerId.trim(),
        companyId: addForm.companyId.trim(),
        businessUnitId: addForm.businessUnitId.trim(),
        departmentIds: addForm.departmentIds,
        dateOfBirth: addForm.dateOfBirth.trim(),
        dateOfEngagement: addForm.dateOfEngagement.trim(),
      };
      await apiService.post<User>(API_ENDPOINTS.USER_MANAGEMENT.BASE, payload);
      const usersResponse = await apiService.get<User[]>(API_ENDPOINTS.USER_MANAGEMENT.BASE);
      setUsers(usersResponse.data);
      handleAddClose();
    } catch (err: unknown) {
      setAddError(getApiErrorMessage(err, couldNotCreate('the user')));
      setError(getApiErrorMessage(err, couldNotCreate('the user')));
    } finally {
      setUpdating(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedUser) return;

    if (!editForm.isActive && !editForm.dateOfExit) {
      setError('Enter a date of exit when deactivating someone.');
      return;
    }

    setUpdating(true);
    setError('');

    try {
      // Ensure Employee role is always included
      const rolesToSave = Array.isArray(editForm.roles) ? [...editForm.roles] : [];
      if (!rolesToSave.includes('Employee')) {
        rolesToSave.push('Employee');
      }

      await apiService.put(API_ENDPOINTS.USER_MANAGEMENT.BY_ID(selectedUser.id), {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        roleTemplateId: editForm.roleTemplateId || null,
        managerId: editForm.managerId || null,
        companyId: editForm.companyId || null,
        businessUnitId: editForm.businessUnitId || null,
        departmentIds: editForm.departmentIds.length > 0 ? editForm.departmentIds : null,
        isActive: editForm.isActive,
        roles: rolesToSave,
        dateOfBirth: editForm.dateOfBirth ? editForm.dateOfBirth : null,
        dateOfEngagement: editForm.dateOfEngagement ? editForm.dateOfEngagement : null,
        dateOfExit: !editForm.isActive && editForm.dateOfExit ? editForm.dateOfExit : undefined,
      });

      // Reload users
      const usersResponse = await apiService.get<User[]>(API_ENDPOINTS.USER_MANAGEMENT.BASE);
      setUsers(usersResponse.data);

      handleEditClose();
    } catch (err: unknown) {
      console.error('Failed to update user:', err);
      setError(getApiErrorMessage(err, couldNotUpdate('the user')));
    } finally {
      setUpdating(false);
    }
  };

  const getRoleTemplateName = useCallback((roleTemplateId: string | undefined) => {
    if (!roleTemplateId) return 'Not assigned';
    const template = roleTemplates.find((t) => t.id === roleTemplateId);
    return template?.name || 'Unknown';
  }, [roleTemplates]);

  const getManagerName = useCallback((managerId: string | undefined) => {
    if (!managerId) return 'Not assigned';
    const manager = managers.find((m) => m.id === managerId);
    return manager?.fullName || 'Unknown';
  }, [managers]);


  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(0); // Reset to first page when sorting changes
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    }
    return sortDirection === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 16 }} />
      : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  const companiesById = useMemo(() => {
    const map = new Map<string, CompanyOption>();
    for (const c of companies) map.set(c.id, c);
    return map;
  }, [companies]);

  const userMissingCompanyForLeave = useCallback(
    (user: User) => user.isActive && isMissingCompanyForLeave(user.companyId, companiesById),
    [companiesById]
  );

  // Counts for admin summary cards (active users only)
  const missingBirthdayCount = useMemo(
    () => users.filter((u) => u.isActive && !u.dateOfBirth).length,
    [users]
  );
  const missingAnniversaryCount = useMemo(
    () => users.filter((u) => u.isActive && !u.dateOfEngagement).length,
    [users]
  );
  const missingExitCount = useMemo(
    () => users.filter((u) => !u.isActive && !u.dateOfExit).length,
    [users]
  );
  const missingEitherCount = useMemo(
    () => users.filter((u) => u.isActive && (!u.dateOfBirth || !u.dateOfEngagement)).length,
    [users]
  );
  const missingCompanyCount = useMemo(
    () => users.filter(userMissingCompanyForLeave).length,
    [users, userMissingCompanyForLeave]
  );

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users;

    // Apply role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter((user) => user.roles.includes(roleFilter));
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      filtered = filtered.filter((user) => user.isActive === isActive);
    }

    // Apply profile complete filter (missing birthday/anniversary)
    if (profileCompleteFilter === 'missing_dates') {
      filtered = filtered.filter(
        (u) => u.isActive && (!u.dateOfBirth || !u.dateOfEngagement)
      );
    } else if (profileCompleteFilter === 'missing_company') {
      filtered = filtered.filter(userMissingCompanyForLeave);
    } else if (profileCompleteFilter === 'missing_exit') {
      filtered = filtered.filter((u) => !u.isActive && !u.dateOfExit);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((user) => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        const email = user.email.toLowerCase();
        const roleTemplateName = getRoleTemplateName(user.roleTemplateId).toLowerCase();
        const managerName = getManagerName(user.managerId).toLowerCase();

        return (
          fullName.includes(query) ||
          email.includes(query) ||
          roleTemplateName.includes(query) ||
          managerName.includes(query)
        );
      });
    }

    // Apply sorting
    if (!sortField) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'name':
          aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
          bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case 'roles':
          aValue = a.roles.join(', ').toLowerCase();
          bValue = b.roles.join(', ').toLowerCase();
          break;
        case 'roleTemplate':
          aValue = getRoleTemplateName(a.roleTemplateId).toLowerCase();
          bValue = getRoleTemplateName(b.roleTemplateId).toLowerCase();
          break;
        case 'manager':
          aValue = getManagerName(a.managerId).toLowerCase();
          bValue = getManagerName(b.managerId).toLowerCase();
          break;
        case 'status':
          aValue = a.isActive ? 1 : 0;
          bValue = b.isActive ? 1 : 0;
          break;
        case 'dateOfBirth':
          aValue = a.dateOfBirth || '';
          bValue = b.dateOfBirth || '';
          break;
        case 'dateOfEngagement':
          aValue = a.dateOfEngagement || '';
          bValue = b.dateOfEngagement || '';
          break;
        case 'lastLogin':
          aValue = a.lastLoginAt || '';
          bValue = b.lastLoginAt || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [users, searchQuery, roleFilter, statusFilter, profileCompleteFilter, sortField, sortDirection, getRoleTemplateName, getManagerName, userMissingCompanyForLeave]);

  // Paginate users
  const paginatedUsers = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAndSortedUsers.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedUsers, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !users.length) {
    return (
      <Box sx={{ p: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mb: 2, ml: -1 }} title="Back">
          <ArrowBackIcon />
        </IconButton>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PeopleIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
            <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
              {currentUser && currentUser.roles.some((r: string) => r === 'Manager' || r === 'SeniorManager') && !currentUser.roles.includes('Administrator')
                ? 'Team Roster'
                : 'User Management'}
            </Typography>
          </Box>
          {currentUser?.roles?.includes('Administrator') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddOpen}
              sx={{ textTransform: 'none' }}
            >
              Add User
            </Button>
          )}
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          {currentUser && currentUser.roles.some((r: string) => r === 'Manager' || r === 'SeniorManager') && !currentUser.roles.includes('Administrator')
            ? 'Manage your team members, view their performance reviews, and configure check-in schedules for your direct reports.'
            : 'Manage all users in the system, assign roles and role templates, set managers, and configure organisational structure.'}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {currentUser?.roles?.includes('Administrator') && (missingBirthdayCount > 0 || missingAnniversaryCount > 0 || missingCompanyCount > 0) && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {missingBirthdayCount > 0 && (
              <Card
                variant="outlined"
                sx={{
                  minWidth: 220,
                  borderColor: 'warning.main',
                  bgcolor: 'warning.50',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' },
                }}
                onClick={() => {
                  setProfileCompleteFilter('missing_dates');
                  setPage(0);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setProfileCompleteFilter('missing_dates');
                    setPage(0);
                  }
                }}
                title="Show users missing birthday or work anniversary"
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <CakeIcon color="warning" fontSize="small" />
                    <Typography variant="subtitle2" color="text.secondary">Missing birthday</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold">{missingBirthdayCount}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active user{missingBirthdayCount !== 1 ? 's' : ''} without date of birth (needed for birthday notifications)
                  </Typography>
                </CardContent>
              </Card>
            )}
            {missingAnniversaryCount > 0 && (
              <Card
                variant="outlined"
                sx={{
                  minWidth: 220,
                  borderColor: 'warning.main',
                  bgcolor: 'warning.50',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' },
                }}
                onClick={() => {
                  setProfileCompleteFilter('missing_dates');
                  setPage(0);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setProfileCompleteFilter('missing_dates');
                    setPage(0);
                  }
                }}
                title="Show users missing birthday or work anniversary"
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <WorkHistoryIcon color="warning" fontSize="small" />
                    <Typography variant="subtitle2" color="text.secondary">Missing work anniversary</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold">{missingAnniversaryCount}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active user{missingAnniversaryCount !== 1 ? 's' : ''} without date of engagement (notifications & leave)
                  </Typography>
                </CardContent>
              </Card>
            )}
            {missingCompanyCount > 0 && (
              <Card
                variant="outlined"
                sx={{
                  minWidth: 220,
                  borderColor: 'warning.main',
                  bgcolor: 'warning.50',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' },
                }}
                onClick={() => {
                  setProfileCompleteFilter('missing_company');
                  setPage(0);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setProfileCompleteFilter('missing_company');
                    setPage(0);
                  }
                }}
                title="Show users missing company or valid country for leave"
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <BusinessIcon color="warning" fontSize="small" />
                    <Typography variant="subtitle2" color="text.secondary">Missing company for leave</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold">{missingCompanyCount}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active user{missingCompanyCount !== 1 ? 's' : ''} without a company or valid country (leave blocked)
                  </Typography>
                </CardContent>
              </Card>
            )}
            {missingExitCount > 0 && (
              <Card
                variant="outlined"
                sx={{
                  minWidth: 220,
                  borderColor: 'warning.main',
                  bgcolor: 'warning.50',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' },
                }}
                onClick={() => {
                  setProfileCompleteFilter('missing_exit');
                  setStatusFilter('inactive');
                  setPage(0);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setProfileCompleteFilter('missing_exit');
                    setStatusFilter('inactive');
                    setPage(0);
                  }
                }}
                title="Show inactive users missing a date of exit"
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <WorkHistoryIcon color="warning" fontSize="small" />
                    <Typography variant="subtitle2" color="text.secondary">Missing date of exit</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold">{missingExitCount}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Inactive user{missingExitCount !== 1 ? 's' : ''} without an exit date (needed for attrition)
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label={currentUser && currentUser.roles.some((r: string) => r === 'Manager' || r === 'SeniorManager') && !currentUser.roles.includes('Administrator')
                  ? "Search team members"
                  : "Search users"}
                placeholder={currentUser && currentUser.roles.some((r: string) => r === 'Manager' || r === 'SeniorManager') && !currentUser.roles.includes('Administrator')
                  ? "Search by name, email, role template, or manager..."
                  : "Search by name, email, role template, or manager..."}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0); // Reset to first page when search changes
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  label="Role"
                  onChange={(e) => {
                    setRoleFilter(e.target.value);
                    setPage(0); // Reset to first page when filter changes
                  }}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  {AVAILABLE_ROLES.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0); // Reset to first page when filter changes
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
              {currentUser?.roles?.includes('Administrator') && (
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>Profile data</InputLabel>
                  <Select
                    value={profileCompleteFilter}
                    label="Profile data"
                    onChange={(e) => {
                      const next = e.target.value;
                      setProfileCompleteFilter(next);
                      if (next === 'missing_exit' && statusFilter === 'active') {
                        setStatusFilter('all');
                      }
                      setPage(0);
                    }}
                  >
                    <MenuItem value="all">All users</MenuItem>
                    <MenuItem value="missing_dates">Missing birthday/anniversary ({missingEitherCount})</MenuItem>
                    <MenuItem value="missing_company">Missing company for leave ({missingCompanyCount})</MenuItem>
                    <MenuItem value="missing_exit">Missing date of exit ({missingExitCount})</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>
            {(searchQuery.trim() !== '' || roleFilter !== 'all' || statusFilter !== 'all' || profileCompleteFilter !== 'all') && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                {searchQuery.trim() !== '' && (
                  <Chip
                    label={`Search: "${searchQuery}"`}
                    onDelete={() => {
                      setSearchQuery('');
                      setPage(0);
                    }}
                    size="small"
                    color="primary"
                  />
                )}
                {roleFilter !== 'all' && (
                  <Chip
                    label={`Role: ${roleFilter}`}
                    onDelete={() => {
                      setRoleFilter('all');
                      setPage(0);
                    }}
                    size="small"
                    color="info"
                  />
                )}
                {statusFilter !== 'all' && (
                  <Chip
                    label={`Status: ${statusFilter === 'active' ? 'Active' : 'Inactive'}`}
                    onDelete={() => {
                      setStatusFilter('all');
                      setPage(0);
                    }}
                    size="small"
                    color={statusFilter === 'active' ? 'success' : 'error'}
                  />
                )}
                {profileCompleteFilter !== 'all' && (
                  <Chip
                    label={
                      profileCompleteFilter === 'missing_company'
                        ? 'Missing company for leave'
                        : profileCompleteFilter === 'missing_exit'
                          ? 'Missing date of exit'
                          : 'Missing birthday/anniversary'
                    }
                    onDelete={() => {
                      setProfileCompleteFilter('all');
                      setPage(0);
                    }}
                    size="small"
                    color="warning"
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>

            <TableContainer component={Paper} variant="outlined">
              <Table key={displayTimeZoneId}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { opacity: 0.7 },
                        }}
                        onClick={() => handleSort('name')}
                      >
                        <strong>Name</strong>
                        {getSortIcon('name')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { opacity: 0.7 },
                        }}
                        onClick={() => handleSort('email')}
                      >
                        <strong>Email</strong>
                        {getSortIcon('email')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { opacity: 0.7 },
                        }}
                        onClick={() => handleSort('roles')}
                      >
                        <strong>Roles</strong>
                        {getSortIcon('roles')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { opacity: 0.7 },
                        }}
                        onClick={() => handleSort('roleTemplate')}
                      >
                        <strong>Role Template</strong>
                        {getSortIcon('roleTemplate')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { opacity: 0.7 },
                        }}
                        onClick={() => handleSort('manager')}
                      >
                        <strong>Manager</strong>
                        {getSortIcon('manager')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { opacity: 0.7 },
                        }}
                        onClick={() => handleSort('status')}
                      >
                        <strong>Status</strong>
                        {getSortIcon('status')}
                      </Box>
                    </TableCell>
                    {currentUser?.roles?.includes('Administrator') && (
                      <>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              userSelect: 'none',
                              '&:hover': { opacity: 0.7 },
                            }}
                            onClick={() => handleSort('dateOfBirth')}
                          >
                            <strong>Birthday</strong>
                            {getSortIcon('dateOfBirth')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              userSelect: 'none',
                              '&:hover': { opacity: 0.7 },
                            }}
                            onClick={() => handleSort('dateOfEngagement')}
                          >
                            <strong>Work anniversary</strong>
                            {getSortIcon('dateOfEngagement')}
                          </Box>
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { opacity: 0.7 },
                        }}
                        onClick={() => handleSort('lastLogin')}
                      >
                        <strong>Last login</strong>
                        {getSortIcon('lastLogin')}
                      </Box>
                    </TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={currentUser?.roles?.includes('Administrator') ? 10 : 8} align="center" sx={{ py: 4, border: 'none' }}>
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            {searchQuery.trim() || roleFilter !== 'all' || statusFilter !== 'all' || profileCompleteFilter !== 'all' ? 'No users match your search or filters.' : 'No users found.'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedUsers.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          {user.firstName} {user.lastName}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          {user.roles.map((role) => (
                            <Chip
                              key={role}
                              label={role}
                              size="small"
                              color={getRoleBadgeColor(role)}
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </TableCell>
                        <TableCell>{getRoleTemplateName(user.roleTemplateId)}</TableCell>
                        <TableCell>{getManagerName(user.managerId)}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.isActive ? 'Active' : 'Inactive'}
                            color={user.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        {currentUser?.roles?.includes('Administrator') && (
                          <>
                            <TableCell sx={{ minWidth: 100 }}>
                              {user.dateOfBirth ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}>
                                  <CakeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  {formatDate(user.dateOfBirth, { format: 'medium' })}
                                </Box>
                              ) : user.isActive ? (
                                <Box component="span" title="Missing date of birth" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                  <WarningAmberIcon fontSize="small" color="warning" />
                                </Box>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                              {user.dateOfEngagement ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}>
                                  <WorkHistoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  {formatDate(user.dateOfEngagement, { format: 'medium' })}
                                </Box>
                              ) : user.isActive ? (
                                <Box component="span" title="Missing work anniversary" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                  <WarningAmberIcon fontSize="small" color="warning" />
                                </Box>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                          </>
                        )}
                        <TableCell>
                          <Typography variant="body2">
                            {user.lastLoginAt
                              ? formatDate(user.lastLoginAt, { includeTime: true })
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleEditClick(user)}>
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredAndSortedUsers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ borderTop: '1px solid', borderColor: 'divider' }}
            />
          </CardContent>
        </Card>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {selectedUser?.lastLoginAt && (
              <Typography variant="body2" color="text.secondary">
                Last signed in: {formatDate(selectedUser.lastLoginAt, { includeTime: true })}
              </Typography>
            )}
            <TextField
              label="First Name"
              value={editForm.firstName}
              onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
              fullWidth
            />
            <TextField
              label="Last Name"
              value={editForm.lastName}
              onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
              fullWidth
            />
            <TextField
              label="Date of birth"
              type="date"
              value={editForm.dateOfBirth}
              onChange={(e) => setEditForm({ ...editForm, dateOfBirth: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Used for birthday notifications"
              sx={{ '& input::-webkit-calendar-picker-indicator': { cursor: 'pointer' } }}
            />
            <TextField
              label="Work anniversary (date of engagement)"
              type="date"
              value={editForm.dateOfEngagement}
              onChange={(e) => setEditForm({ ...editForm, dateOfEngagement: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Used for anniversary notifications and leave"
              sx={{ '& input::-webkit-calendar-picker-indicator': { cursor: 'pointer' } }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editForm.isActive}
                  onChange={(e) => {
                    const active = e.target.checked;
                    setEditForm({
                      ...editForm,
                      isActive: active,
                      dateOfExit: active
                        ? ''
                        : editForm.dateOfExit || new Date().toISOString().slice(0, 10),
                    });
                  }}
                />
              }
              label="Active"
            />
            {!editForm.isActive && (
              <TextField
                label="Date of exit"
                type="date"
                value={editForm.dateOfExit}
                onChange={(e) => setEditForm({ ...editForm, dateOfExit: e.target.value })}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                helperText="Last day of employment. Used for attrition reporting. Reactivating clears this date."
                sx={{ '& input::-webkit-calendar-picker-indicator': { cursor: 'pointer' } }}
              />
            )}
            <FormControl fullWidth>
              <InputLabel id="role-template-label">Role Template</InputLabel>
              <Select
                labelId="role-template-label"
                id="role-template"
                value={editForm.roleTemplateId}
                label="Role Template"
                onChange={(e) => setEditForm({ ...editForm, roleTemplateId: e.target.value })}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {roleTemplates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Roles
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  (All users automatically have the Employee role)
                </Typography>
              </Box>
              <FormGroup>
                {AVAILABLE_ROLES.map((role) => {
                  const isEmployee = role === 'Employee';
                  const isChecked = Array.isArray(editForm.roles) && editForm.roles.includes(role);
                  return (
                    <FormControlLabel
                      key={role}
                      control={
                        <Checkbox
                          checked={isChecked}
                          disabled={isEmployee}
                          onChange={(e) => {
                            if (isEmployee) return; // Prevent changes to Employee role
                            const currentRoles = Array.isArray(editForm.roles) ? editForm.roles : [];
                            if (e.target.checked) {
                              setEditForm({ ...editForm, roles: [...currentRoles, role] });
                            } else {
                              setEditForm({ ...editForm, roles: currentRoles.filter(r => r !== role) });
                            }
                          }}
                        />
                      }
                      label={role}
                    />
                  );
                })}
              </FormGroup>
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'action.hover',
                    '&:hover': { backgroundColor: 'action.selected' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon color="primary" fontSize="small" />
                    <Typography variant="body2" fontWeight={500}>
                      Role Permissions Reference
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {AVAILABLE_ROLES.map((role) => {
                      const roleInfo = ROLE_DESCRIPTIONS[role];
                      if (!roleInfo) return null;
                      return (
                        <Box key={role} sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 2 }}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            {roleInfo.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            {roleInfo.description}
                          </Typography>
                          <Typography variant="caption" fontWeight={500} display="block" sx={{ mb: 0.5 }}>
                            Permissions:
                          </Typography>
                          <Box component="ul" sx={{ m: 0, pl: 2 }}>
                            {roleInfo.permissions.map((permission, idx) => (
                              <Typography key={idx} component="li" variant="caption" color="text.secondary">
                                {permission}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
            <Autocomplete
              options={managers.filter((manager) => manager.id !== selectedUser?.id)}
              getOptionLabel={(option) => `${option.fullName} (${option.email})`}
              value={selectedManager}
              onChange={(_, newValue) => {
                setSelectedManager(newValue);
                setEditForm({ ...editForm, managerId: newValue?.id || '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Manager"
                  placeholder="Search for a manager..."
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="No managers found"
              clearOnEscape
              clearText="Clear"
            />
            <FormControl fullWidth>
              <InputLabel id="company-label">Company</InputLabel>
              <Select
                labelId="company-label"
                id="company"
                value={editForm.companyId}
                label="Company"
                onChange={(e) => setEditForm({ ...editForm, companyId: e.target.value })}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="business-unit-label">Business Unit</InputLabel>
              <Select
                labelId="business-unit-label"
                id="business-unit"
                value={editForm.businessUnitId}
                label="Business Unit"
                onChange={(e) => setEditForm({ ...editForm, businessUnitId: e.target.value })}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {businessUnits.map((businessUnit) => (
                  <MenuItem key={businessUnit.id} value={businessUnit.id}>
                    {businessUnit.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="department-label">Department</InputLabel>
              <Select
                labelId="department-label"
                id="department"
                multiple
                value={editForm.departmentIds}
                label="Department"
                onChange={(e) => {
                  const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                  setEditForm({ ...editForm, departmentIds: value });
                }}
                renderValue={(selected) => {
                  if (selected.length === 0) return '';
                  return (selected as string[])
                    .map((id) => departments.find((d) => d.id === id)?.name)
                    .filter(Boolean)
                    .join(', ');
                }}
              >
                {departments.map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    <Checkbox checked={editForm.departmentIds.indexOf(department.id) > -1} />
                    {department.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={updating} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleEditSubmit} variant="contained" disabled={updating} sx={{ textTransform: 'none' }}>
            {updating ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
        </Dialog>

        {/* Add User Dialog */}
        <Dialog open={addDialogOpen} onClose={handleAddClose} maxWidth="sm" fullWidth>
          <DialogTitle>Add User</DialogTitle>
          <DialogContent>
            {addError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddError('')}>
                {addError}
              </Alert>
            )}
            {SHOW_STAGING_DEFAULT_PASSWORD_HINT && (
              <Alert severity="info" sx={{ mb: 2 }}>
                New users can sign in with their email and the staging default password:{' '}
                <strong>{STAGING_DEFAULT_USER_PASSWORD}</strong>
              </Alert>
            )}
            <Box
              component="form"
              id="add-user-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleAddSubmit();
              }}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
            >
              <TextField
                label="Email"
                type="email"
                value={addForm.email}
                onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                fullWidth
                required
                autoFocus
              />
              <TextField
                label="First Name"
                value={addForm.firstName}
                onChange={(e) => setAddForm({ ...addForm, firstName: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Last Name"
                value={addForm.lastName}
                onChange={(e) => setAddForm({ ...addForm, lastName: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Date of birth"
                type="date"
                value={addForm.dateOfBirth}
                onChange={(e) => setAddForm({ ...addForm, dateOfBirth: e.target.value })}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                helperText="Used for birthday notifications"
                sx={{ '& input::-webkit-calendar-picker-indicator': { cursor: 'pointer' } }}
              />
              <TextField
                label="Work anniversary (date of engagement)"
                type="date"
                value={addForm.dateOfEngagement}
                onChange={(e) => setAddForm({ ...addForm, dateOfEngagement: e.target.value })}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                helperText="Used for anniversary notifications and leave"
                sx={{ '& input::-webkit-calendar-picker-indicator': { cursor: 'pointer' } }}
              />
              <FormControl fullWidth required>
                <InputLabel id="add-role-template-label">Role Template</InputLabel>
                <Select
                  labelId="add-role-template-label"
                  value={addForm.roleTemplateId}
                  label="Role Template"
                  onChange={(e) => setAddForm({ ...addForm, roleTemplateId: e.target.value })}
                >
                  {roleTemplates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Roles *
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (Employee is always included)
                  </Typography>
                </Box>
                <FormGroup>
                  {AVAILABLE_ROLES.map((role) => {
                    const isEmployee = role === 'Employee';
                    const isChecked = Array.isArray(addForm.roles) && addForm.roles.includes(role);
                    return (
                      <FormControlLabel
                        key={role}
                        control={
                          <Checkbox
                            checked={isChecked}
                            disabled={isEmployee}
                            onChange={(e) => {
                              if (isEmployee) return;
                              const current = Array.isArray(addForm.roles) ? addForm.roles : [];
                              if (e.target.checked) {
                                setAddForm({ ...addForm, roles: [...current, role] });
                              } else {
                                setAddForm({ ...addForm, roles: current.filter((r) => r !== role) });
                              }
                            }}
                          />
                        }
                        label={role}
                      />
                    );
                  })}
                </FormGroup>
              </Box>
              <Autocomplete
                options={managers}
                getOptionLabel={(option) => `${option.fullName} (${option.email})`}
                value={addFormManager}
                onChange={(_, newValue) => {
                  setAddFormManager(newValue);
                  setAddForm({ ...addForm, managerId: newValue?.id || '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Manager"
                    placeholder="Search for a manager..."
                    required
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No managers found"
              />
              <FormControl fullWidth required>
                <InputLabel id="add-company-label">Company</InputLabel>
                <Select
                  labelId="add-company-label"
                  value={addForm.companyId}
                  label="Company"
                  onChange={(e) => setAddForm({ ...addForm, companyId: e.target.value })}
                >
                  {companies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel id="add-bu-label">Business Unit</InputLabel>
                <Select
                  labelId="add-bu-label"
                  value={addForm.businessUnitId}
                  label="Business Unit"
                  onChange={(e) => setAddForm({ ...addForm, businessUnitId: e.target.value })}
                >
                  {businessUnits.map((bu) => (
                    <MenuItem key={bu.id} value={bu.id}>{bu.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel id="add-dept-label">Department</InputLabel>
                <Select
                  labelId="add-dept-label"
                  multiple
                  value={addForm.departmentIds}
                  label="Department"
                  onChange={(e) => {
                    const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    setAddForm({ ...addForm, departmentIds: value });
                  }}
                  renderValue={(selected) =>
                    (selected as string[])
                      .map((id) => departments.find((d) => d.id === id)?.name)
                      .filter(Boolean)
                      .join(', ') || ''
                  }
                >
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      <Checkbox checked={addForm.departmentIds.indexOf(d.id) > -1} />
                      {d.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddClose} disabled={updating} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="add-user-form"
              variant="contained"
              disabled={updating || !isAddFormComplete}
              sx={{ textTransform: 'none' }}
            >
              {updating ? <CircularProgress size={24} /> : 'Add User'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AppLayout>
  );
}

